import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { SHAPE_CONFIGS } from './TableShape';
import { Save, RotateCw, Trash2, X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

export default function TablePropertiesPanel({ 
  table, 
  guests = [],
  onUpdate, 
  onDelete,
  onClose 
}) {
  const { adminDarkMode } = useTheme();
  const [form, setForm] = useState({
    table_name: '',
    shape: 'round',
    seats: 8,
    rotation: 0,
    width: '',
    height: '',
  });
  const [saving, setSaving] = useState(false);

  // Initialize form when table changes
  useEffect(() => {
    if (table) {
      setForm({
        table_name: table.table_name || '',
        shape: table.shape || 'round',
        seats: table.seats || table.capacity || 8,
        rotation: table.rotation || 0,
        width: table.width || '',
        height: table.height || '',
      });
    }
  }, [table]);

  if (!table) {
    return (
      <div className={`${adminDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-white border-slate-200'} p-4 rounded-lg border h-full`}>
        <div className={`flex items-center justify-center h-full ${adminDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
          <p className="text-sm text-center">
            Select a table on the canvas<br />to edit its properties
          </p>
        </div>
      </div>
    );
  }

  const tableGuests = guests.filter(g => g.table_id === table.id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onUpdate(table.id, {
        table_name: form.table_name,
        shape: form.shape,
        seats: parseInt(form.seats) || 8,
        rotation: parseInt(form.rotation) || 0,
        capacity: parseInt(form.seats) || 8, // Sync capacity with seats
        width: form.width ? parseInt(form.width) : null,
        height: form.height ? parseInt(form.height) : null,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRotate = () => {
    const newRotation = (form.rotation + 45) % 360;
    setForm(prev => ({ ...prev, rotation: newRotation }));
    onUpdate(table.id, { rotation: newRotation });
  };

  return (
    <div className={`${adminDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-white border-slate-200 shadow-lg'} p-4 rounded-lg border h-full overflow-auto`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className={`text-sm font-semibold ${adminDarkMode ? 'text-white' : 'text-slate-800'}`}>Table Properties</h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className={`h-6 w-6 p-0 ${adminDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-800'}`}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Table Name */}
        <div className="space-y-2">
          <Label className={`text-xs ${adminDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Table Name</Label>
          <Input
            value={form.table_name}
            onChange={(e) => setForm(prev => ({ ...prev, table_name: e.target.value }))}
            className={`h-8 text-sm ${adminDarkMode ? 'bg-slate-800/50 border-slate-700 text-white' : 'bg-slate-50 border-slate-200 text-slate-800'}`}
            placeholder="Table 1"
            data-testid="props-table-name"
          />
        </div>

        {/* Shape - Hide for grouped tables */}
        {table.shape !== 'grouped' && (
          <div className="space-y-2">
            <Label className={`text-xs ${adminDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Shape</Label>
            <Select 
              value={form.shape || 'round'} 
              onValueChange={(val) => {
                // Ignore empty values - Radix Select can sometimes trigger with empty string
                if (val && val.trim() !== '') {
                  setForm(prev => ({ ...prev, shape: val }));
                }
              }}
            >
              <SelectTrigger className={`h-8 text-sm ${adminDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-50 border-slate-200'}`} data-testid="props-shape-select">
                <SelectValue placeholder="Select shape" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(SHAPE_CONFIGS).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        
        {/* Grouped Table Info */}
        {table.shape === 'grouped' && (
          <div className="space-y-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-yellow-500" />
              <span className="text-yellow-600 text-xs font-medium">Grouped Table</span>
            </div>
            <p className={`text-xs ${adminDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              This table is composed of {table.grouped_piece_ids?.length || 0} custom pieces.
              Drag the bounding box to move all pieces together.
            </p>
          </div>
        )}

        {/* Seats */}
        <div className="space-y-2">
          <Label className={`text-xs ${adminDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Number of Seats</Label>
          <Input
            type="number"
            min="1"
            max="50"
            value={form.seats}
            onChange={(e) => setForm(prev => ({ ...prev, seats: e.target.value }))}
            className={`h-8 text-sm ${adminDarkMode ? 'bg-slate-800/50 border-slate-700 text-white' : 'bg-slate-50 border-slate-200 text-slate-800'}`}
            data-testid="props-seats"
          />
        </div>

        {/* Size (Width x Height) - Hide for grouped tables */}
        {table.shape !== 'grouped' && (
          <div className="space-y-2">
            <Label className={`text-xs ${adminDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              Size (px) 
              <span className={`ml-1 ${adminDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                default: {SHAPE_CONFIGS[form.shape]?.width || 80}×{SHAPE_CONFIGS[form.shape]?.height || 80}
              </span>
            </Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="40"
                max="300"
                value={form.width}
                onChange={(e) => setForm(prev => ({ ...prev, width: e.target.value }))}
                placeholder={String(SHAPE_CONFIGS[form.shape]?.width || 80)}
                className={`h-8 text-sm flex-1 ${adminDarkMode ? 'bg-slate-800/50 border-slate-700 text-white' : 'bg-slate-50 border-slate-200 text-slate-800'}`}
                data-testid="props-width"
              />
              <span className={`text-xs ${adminDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>×</span>
              <Input
                type="number"
                min="40"
                max="300"
                value={form.height}
                onChange={(e) => setForm(prev => ({ ...prev, height: e.target.value }))}
                placeholder={String(SHAPE_CONFIGS[form.shape]?.height || 80)}
                className={`h-8 text-sm flex-1 ${adminDarkMode ? 'bg-slate-800/50 border-slate-700 text-white' : 'bg-slate-50 border-slate-200 text-slate-800'}`}
                data-testid="props-height"
              />
            </div>
          </div>
        )}

        {/* Rotation - Hide for grouped tables */}
        {table.shape !== 'grouped' && (
          <div className="space-y-2">
            <Label className={`text-xs ${adminDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Rotation</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="0"
                max="359"
                step="15"
                value={form.rotation}
                onChange={(e) => setForm(prev => ({ ...prev, rotation: e.target.value }))}
                className={`h-8 text-sm flex-1 ${adminDarkMode ? 'bg-slate-800/50 border-slate-700 text-white' : 'bg-slate-50 border-slate-200 text-slate-800'}`}
                data-testid="props-rotation"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRotate}
                className={`h-8 px-2 ${adminDarkMode ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-200 text-slate-600 hover:bg-slate-100'}`}
                data-testid="props-rotate-btn"
              >
                <RotateCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Save Button */}
        <Button
          type="submit"
          size="sm"
          disabled={saving}
          className="w-full bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F] h-8"
          data-testid="props-save-btn"
        >
          <Save className="h-4 w-4 mr-1" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>

        {/* Delete Button */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onDelete(table.id)}
          className={`w-full h-8 ${adminDarkMode ? 'border-red-900/50 text-red-400 hover:bg-red-900/20' : 'border-red-200 text-red-500 hover:bg-red-50'}`}
          data-testid="props-delete-btn"
        >
          <Trash2 className="h-4 w-4 mr-1" />
          Remove from Layout
        </Button>
      </form>

      {/* Assigned Guests */}
      <div className={`mt-6 pt-4 border-t ${adminDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
        <h4 className={`text-xs font-semibold mb-2 ${adminDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
          Assigned Guests ({tableGuests.length}/{form.seats})
        </h4>
        {tableGuests.length === 0 ? (
          <p className={`text-xs ${adminDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>No guests assigned</p>
        ) : (
          <div className="space-y-1 max-h-32 overflow-auto">
            {tableGuests.map(guest => (
              <div 
                key={guest.id}
                className={`text-xs truncate py-0.5 ${adminDarkMode ? 'text-slate-300' : 'text-slate-600'}`}
              >
                {guest.full_name}
              </div>
            ))}
          </div>
        )}
        {tableGuests.length > parseInt(form.seats) && (
          <p className="text-xs text-amber-500 mt-2">
            Warning: More guests than seats!
          </p>
        )}
      </div>

      {/* Position Info */}
      <div className={`mt-4 pt-4 border-t ${adminDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
        <h4 className={`text-xs font-semibold mb-2 ${adminDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Position</h4>
        <div className={`grid grid-cols-2 gap-2 text-xs ${adminDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
          <span>X: {table.position_x || 0}px</span>
          <span>Y: {table.position_y || 0}px</span>
        </div>
      </div>
    </div>
  );
}
